/**
 * Quais recursos esta aba está observando (H02, C21).
 *
 * ── Assinatura por RECURSO, e não "me mande tudo" ─────────────────────────
 *
 * A C21 autoriza cada assinatura individualmente na abertura do fluxo: recurso
 * de outro tenant recusa o fluxo inteiro com 404. Isso só funciona se o cliente
 * pedir o que precisa — um "assine tudo" não teria o que autorizar.
 *
 * E há a razão prática: uma revenda movimentada produz notificação demais. A
 * tela que só mostra a conversa 42 não ganha nada com o aviso das outras
 * trezentas, e o custo de recebê-las é rebusca inútil.
 *
 * ── Contagem, e não conjunto ──────────────────────────────────────────────
 *
 * Dois componentes podem observar a mesma conversa — a tela de atendimento e o
 * bloco de conversas do 360. Se a saída de um removesse a assinatura, o outro
 * pararia de receber sem que nada aparentasse errado.
 *
 * Por isso cada `observar` soma e cada saída subtrai; a assinatura só cai
 * quando o último interessado sai.
 */

import { useEffect, useSyncExternalStore } from 'react';

const contagem = new Map<string, number>();
const ouvintes = new Set<() => void>();

/**
 * A lista é memorizada porque o `useSyncExternalStore` compara por identidade.
 * Montar um array novo a cada leitura faria o React renderizar sem parar.
 */
let instantaneo: readonly string[] = [];

function recalcular(): void {
  instantaneo = [...contagem.keys()].sort();
  for (const ouvinte of ouvintes) ouvinte();
}

export function observarRecurso(assinatura: string): () => void {
  contagem.set(assinatura, (contagem.get(assinatura) ?? 0) + 1);
  if (contagem.get(assinatura) === 1) recalcular();

  return () => {
    const atual = contagem.get(assinatura);
    if (atual === undefined) return;

    if (atual <= 1) {
      contagem.delete(assinatura);
      recalcular();
    } else {
      contagem.set(assinatura, atual - 1);
    }
  };
}

/** Só para teste. */
export function esquecerAssinaturas(): void {
  contagem.clear();
  recalcular();
}

export function assinaturasAtuais(): readonly string[] {
  return instantaneo;
}

function inscrever(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

export function useAssinaturas(): { assinaturasAtuais: readonly string[] } {
  return {
    assinaturasAtuais: useSyncExternalStore(inscrever, assinaturasAtuais, assinaturasAtuais),
  };
}

/**
 * Declara que este componente está olhando um recurso.
 *
 * `null` desliga — é o caso da tela de atendimento sem conversa selecionada,
 * que não deve assinar nada. Sem o `null`, cada tela precisaria de um `if` em
 * volta do hook, e hook condicional não é permitido.
 */
export function useObservarRecurso(assinatura: string | null): void {
  useEffect(() => {
    if (!assinatura) return;
    return observarRecurso(assinatura);
  }, [assinatura]);
}

/**
 * Vários de uma vez — a lista observa todas as caixas que está mostrando.
 *
 * A chave do efeito é a lista ORDENADA e concatenada, não o array: um array
 * novo com o mesmo conteúdo é comum a cada renderização, e usá-lo como
 * dependência refaria a assinatura a cada quadro. O `assinaturasAtuais` já
 * devolve ordenado, e aqui a ordenação se repete para quem passa outra fonte.
 */
export function useObservarRecursos(assinaturas: readonly string[]): void {
  const chave = [...assinaturas].sort().join(',');

  useEffect(() => {
    if (!chave) return;
    const cancelar = chave.split(',').map((a) => observarRecurso(a));
    return () => cancelar.forEach((c) => c());
  }, [chave]);
}
